import * as React from "react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"
import { useResolveCls } from "../contexts/breakpoint-context"
import { ElementRenderer } from "./element-renderer"

export function TabsRenderer({ element }: RendererProps) {
  const { dispatchEvent, resolveSymbol, navigate } = useRenderContext()
  const rcls = useResolveCls()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("tabs")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Support both flat children (legacy) and nested tabs-list/tabs-content structure (v2)
  const rawChildren = element.children ?? []
  const tabsListEl = rawChildren.find(c => c.type === "tabs-list")
  const tabs = tabsListEl?.children ?? rawChildren
  const tabContents = rawChildren.filter(c => c.type === "tabs-content")
  const bindRoute = element.bindRoute === true
  const variant = element.variant as string | undefined

  // State binding via useBoundValue (1-cycle sync, no useEffect)
  const [boundVal, setBoundVal] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  // Route binding via useBoundValue (1-cycle sync, no useEffect)
  const [routeVal] = useBoundValue<string>(
    bindRoute ? "_system.navigation.currentRoute" : undefined,
    resolveSymbol,
    "/",
    String,
  )

  // Priority: bound state > route > explicit default > active variant > first tab
  // Note: boundVal can be "" (empty string) which is a valid sync target (SYNC-03 bug fix)
  const explicitDefault = element.defaultValue as string | undefined
  const value =
    (element.bind !== undefined ? boundVal : undefined)
    ?? (bindRoute ? routeVal : undefined)
    ?? explicitDefault
    ?? tabs.find(t => t.variant === "active")?.value
    ?? tabs.find(t => t.variant === "active")?.id
    ?? tabs[0]?.value
    ?? tabs[0]?.id
    ?? "0"

  const handleValueChange = (newValue: string) => {
    setBoundVal(newValue)
    if (bindRoute && navigate) {
      navigate(newValue)
    }
    dispatchEvent(element.id, "change", { value: newValue })
  }

  // If tabs-list has its own className, use it; otherwise let shadcn TabsList defaults apply
  const listClasses = tabsListEl?.className
    ? rcls(tabsListEl.className)
    : undefined

  return (
    <Tabs
      {...(element.bind !== undefined
        ? { value, onValueChange: handleValueChange }
        : { defaultValue: value, onValueChange: handleValueChange }
      )}
      className={cn("cn-tabs group/tabs flex flex-col", element.className)}
      data-atom-type="tabs"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <TabsList variant={variant === "line" ? "line" : "default"} className={cn(listClasses)}>
        {tabs.map((child) => {
          const tabValue = child.value ?? child.id
          const isActive = value === tabValue

          // Resolve icon if present
          const iconName = child.icon as string | undefined
          const IconComponent = iconName ? resolveIcon(iconName) : undefined

          // Resolve text
          const text = child.text
            ? String(resolveSymbol(child.text) ?? child.text)
            : ""

          return (
            <TabsTrigger
              key={child.id}
              value={tabValue}
              className={cn(rcls(child.className))}
              data-route={tabValue}
              data-active={isActive || undefined}
            >
              {IconComponent && React.createElement(IconComponent, { className: "size-4" })}
              {text}
            </TabsTrigger>
          )
        })}
      </TabsList>
      {tabContents.map((content) => {
        const contentValue = content.value ?? content.id ?? ""
        return (
          <TabsContent
            key={content.id}
            value={contentValue}
            className={cn(content.className)}
          >
            {content.children?.map((child) => (
              <ElementRenderer key={child.id} element={child} />
            ))}
          </TabsContent>
        )
      })}
    </Tabs>
  )
}
